import React, { useState, useRef, useEffect } from "react";
import api from "../services/api";
import { Sparkles, Send, Bot, User, Loader2, BookOpen, Trash2, Image as ImageIcon, X, Mic } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { marked } from "marked";
import renderMathInElement from "katex/dist/contrib/auto-render";
import { jwtDecode } from "jwt-decode";
import { getToken } from "../services/auth";
import { useBranding } from "../context/BrandingContext";

// Rich Text + Math Renderer Helper for AI messages
function MessageBubble({ text, role, image }) {
  const containerRef = useRef(null);
  const isAssistant = role === "assistant";

  // Safely render markdown to HTML
  const htmlContent = isAssistant ? marked.parse(text) : text;

  useEffect(() => {
    if (containerRef.current) {
      try {
        renderMathInElement(containerRef.current, {
          delimiters: [
            { left: "$$", right: "$$", display: true },
            { left: "\\(", right: "\\)", display: false },
            { left: "$", right: "$", display: false }
          ],
          throwOnError: false
        });
      } catch (e) { }
    }
  }, [text]);

  return (
    <div className={`flex items-start gap-4 max-w-3xl ${isAssistant ? "mr-auto" : "ml-auto flex-row-reverse"}`}>
      <div className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center shadow-sm ${isAssistant ? "bg-gradient-to-br from-indigo-600 to-violet-600 text-white" : "bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400"
        }`}>
        {isAssistant ? <Bot size={20} /> : <User size={20} />}
      </div>

      <div className={`group relative flex flex-col ${isAssistant ? "items-start" : "items-end"}`}>
        <div className={`px-5 py-3.5 rounded-2xl shadow-sm text-sm leading-relaxed ${isAssistant
          ? "bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 text-slate-800 dark:text-slate-200 rounded-tl-none"
          : "bg-indigo-600 text-white rounded-tr-none"
          }`}>
          {image && (
            <div className="mb-3 max-w-xs overflow-hidden rounded-lg border border-black/10 dark:border-white/10">
              <img src={image} alt="User input" className="w-full object-contain" />
            </div>
          )}
          <div
            ref={containerRef}
            className={`prose prose-sm max-w-none ${isAssistant ? "dark:prose-invert" : "prose-invert text-white"}`}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        </div>
        <span className="text-[10px] text-slate-400 dark:text-slate-600 font-bold mt-1 px-1 uppercase tracking-wider">
          {isAssistant ? "AI Assistant" : "You"}
        </span>
      </div>
    </div>
  );
}

export default function AITutor() {
  const [userRole, setUserRole] = useState("student");
  const { branding } = useBranding();

  useEffect(() => {
    try {
      const token = getToken();
      if (token) setUserRole(jwtDecode(token).role || "student");
    } catch (err) { }
  }, []);

  const [input, setInput] = useState("");
  const [stagedImage, setStagedImage] = useState(null); // Holds file and local URL
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const fileInputRef = useRef(null);
  const [history, setHistory] = useState([
    { role: "assistant", content: `Hi! I'm your **${branding.instituteName || "Antigravity"} AI Tutor**. 🚀 \n\nI have studied your course materials, textbooks, and reference documents. What can I help you learn today?` }
  ]);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history]);

  // Voice Recognition Logic
  const toggleVoice = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in this browser. Please try Chrome or Edge.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      console.log("Speech Recognition Started");
      setIsListening(true);
    };

    recognition.onend = () => {
      console.log("Speech Recognition Ended");
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        setInput("⚠️ Microphone blocked. Please allow mic permissions in your browser address bar.");
      } else {
        setInput(`⚠️ Voice Error: ${event.error}`);
      }
    };

    recognition.onresult = (event) => {
      let interimTranscript = "";
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }

      if (finalTranscript) {
        // Commit completed phrases to official input stream
        setInput(prev => prev ? `${prev} ${finalTranscript}` : finalTranscript);
      } else if (interimTranscript) {
        // While speaking, show transient typing effect immediately in the console or slightly ahead
        // Optionally append directly for maximum visual WOW:
        // setInput(prev => prev + interimTranscript); 
        // Better UX: Just use current input + interim for standard real-time feedback!
      }

      // Highly robust merging strategy
      let currentSessionTranscript = "";
      for (let i = 0; i < event.results.length; ++i) {
        currentSessionTranscript += event.results[i][0].transcript + " ";
      }
      // Force immediate UI update with everything transcribed so far in current block
      setInput(currentSessionTranscript.trim());
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setStagedImage({
      file: file,
      url: URL.createObjectURL(file)
    });
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if ((!input.trim() && !stagedImage) || loading) return;

    const userMsg = input;
    const userImg = stagedImage;
    setInput("");
    setStagedImage(null);

    // Record entry into local history for display
    const newHistory = [...history, { role: "user", content: userMsg || (userImg ? "[Sent Image]" : ""), image: userImg?.url }];
    setHistory(newHistory);
    setLoading(true);

    try {
      let base64Payload = null;

      // Convert file to base64 if it was selected
      if (userImg?.file) {
        base64Payload = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(userImg.file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (err) => reject(err);
        });
      }

      const res = await api.post("/ai/chat", {
        messages: newHistory.map(h => ({ role: h.role, content: h.content })),
        image_base64: base64Payload
      });

      setHistory([...newHistory, { role: "assistant", content: res.data.response || res.data.reply }]);
    } catch (err) {
      setHistory([...newHistory, { role: "assistant", content: "⚠️ Sorry, I ran into a connection issue while analyzing that. Please try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    setHistory([
      { role: "assistant", content: "Memory cleared! Let's start fresh. What's on your mind?" }
    ]);
  };

  return (
    <div className="flex h-[calc(100vh-64px)] bg-slate-50 dark:bg-slate-950 overflow-hidden relative">
      
      <main className="flex-1 flex flex-col h-full relative min-w-0">
        {/* Header */}
        <header className="flex-shrink-0 h-16 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/80 backdrop-blur-md flex items-center justify-between px-6 z-10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-600/10 rounded-lg flex items-center justify-center text-indigo-600">
              <Sparkles size={18} fill="currentColor" />
            </div>
            <div>
              <h1 className="font-black text-slate-900 dark:text-white tracking-tight leading-none">AI Study Assistant</h1>
              <p className="text-[10px] text-emerald-600 font-bold flex items-center gap-1 mt-1 uppercase">
                <BookOpen size={10} /> RAG Brain Active
              </p>
            </div>
          </div>
          <button
            onClick={clearHistory}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
            title="Clear Chat"
          >
            <Trash2 size={18} />
          </button>
        </header>

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 no-scrollbar">
          <AnimatePresence initial={false}>
            {history.map((msg, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <MessageBubble text={msg.content} role={msg.role} image={msg.image} />
              </motion.div>
            ))}
          </AnimatePresence>

          {loading && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="flex items-center gap-3 text-indigo-600 dark:text-indigo-400 text-sm font-bold italic px-12"
            >
              <Loader2 size={16} className="animate-spin" />
              Thinking...
            </motion.div>
          )}
          <div ref={bottomRef} className="h-4" />
        </div>

        {/* Input Area */}
        <div className="flex-shrink-0 p-4 bg-gradient-to-t from-slate-50 dark:from-slate-950 to-transparent">
          <form
            onSubmit={handleSendMessage}
            className="max-w-4xl mx-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl shadow-indigo-600/5 flex flex-col p-1.5 transition-all focus-within:ring-2 ring-indigo-500/30"
          >
            {stagedImage && (
              <div className="p-3 border-b border-slate-100 dark:border-slate-800 relative flex items-start">
                <div className="relative group">
                  <img src={stagedImage.url} alt="staged" className="h-20 w-20 object-cover rounded-lg shadow-sm border border-slate-200 dark:border-slate-700" />
                  <button
                    type="button"
                    onClick={() => setStagedImage(null)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600 transition-colors"
                  >
                    <X size={12} />
                  </button>
                </div>
              </div>
            )}
            <div className="flex items-end w-full">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                className="hidden"
                onChange={handleImageSelect}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                title="Attach Problem Image"
                className="flex-shrink-0 w-10 h-10 flex items-center justify-center text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl transition-all mb-0.5"
              >
                <ImageIcon size={19} />
              </button>

              <button
                type="button"
                onClick={toggleVoice}
                title="Speak your question"
                className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl transition-all mb-0.5 mr-1 ${isListening
                  ? "bg-red-50 dark:bg-red-950 text-red-500 animate-pulse"
                  : "text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-800"
                  }`}
              >
                <Mic size={19} />
              </button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                rows={1}
                placeholder="Ask anything about your lessons, solve equations, or brainstorm topics..."
                className="flex-1 bg-transparent outline-none border-none px-4 py-3 text-sm text-slate-800 dark:text-slate-200 resize-none max-h-32"
              />
              <button
                type="submit"
                disabled={(!input.trim() && !stagedImage) || loading}
                className="flex-shrink-0 w-10 h-10 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl flex items-center justify-center transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed mb-0.5"
              >
                <Send size={16} />
              </button>
            </div>
          </form>
          <p className="text-center text-[10px] text-slate-500 mt-3">
            {branding.instituteName || "System"} AI cross-references your actual course materials. Answers are powered by NVIDIA NIM & Pinecone RAG.
          </p>
        </div>
      </main>
    </div>
  );
}
